import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useDebouncedCallback } from 'use-debounce';
import { Loader2, X } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { checkSpanishLocale, cn } from 'lib/utils';
import { getQuestionnaireOptionSearchV3 } from 'services/profile/api';
import { QUESTIONNAIRE_CODE } from 'lib/constants';
import { MedicineWithDoses } from 'services/profile/type';
import { Input } from 'components/ui/input';
import Loader from 'components/Loader';
import { ScrollArea } from 'components/ui/scroll-area';
import Checkmark from './Checkmark';
import { Button } from 'components/ui/button';
import { Medication } from '../SpecifyMedications';
import { usePostQuestionnaireOptionSearchV3 } from 'services/profile/hooks';
import { useTranslation } from 'react-i18next';

interface MedicationSearchProps {
  open: boolean;
  searchOptions: {
    en_choices: string[];
    es_choices: string[];
  };
  onClose: () => void;
  setSelected: React.Dispatch<React.SetStateAction<Medication[]>>;
}

export default function MedicationSearch({
  open,
  onClose,
  searchOptions,
  setSelected,
}: MedicationSearchProps) {
  const { t } = useTranslation();
  const isSpanish = checkSpanishLocale();

  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [conditions, setConditions] = useState<{
    en_choices: string[];
    es_choices: string[];
  }>({
    en_choices: [],
    es_choices: [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { mutate, isPending } = usePostQuestionnaireOptionSearchV3({
    onSuccess: (data) => {
      const medicines: Medication[] = (data as MedicineWithDoses[]).map(
        (medicine) => ({
          ...medicine,
          selectedDose: medicine?.doses ? medicine.doses[0].id : '',
          quantity: 1,
        })
      );
      setSelected(medicines);
      onDialogClose();
    },
    onError: () => {},
  });

  useEffect(() => {
    setConditions(searchOptions);
  }, [searchOptions]);

  const onHandleSelection = (condition: string) => {
    if (selectedConditions.includes(condition)) {
      setSelectedConditions(selectedConditions.filter((c) => c !== condition));
    } else {
      setSelectedConditions([...selectedConditions, condition]);
    }
  };

  const onClearAll = () => setSelectedConditions([]);

  const onDialogClose = () => {
    setSelectedConditions([]);
    setSearchValue('');
    onClose();
  };

  const debouncedSearch = useDebouncedCallback(async (value) => {
    try {
      setIsLoading(true);
      const res = (await getQuestionnaireOptionSearchV3({
        code: QUESTIONNAIRE_CODE.SPECIFY_MEDICATIONS,
        search_text: value,
      })) as any;
      setConditions(res);
    } catch (error) {
      setConditions(searchOptions);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  const onSearch = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const onAdd = () => {
    setSelectedConditions([...selectedConditions, searchValue]);
    setSearchValue('');
    debouncedSearch('');
  };

  const onApply = () => {
    mutate({ combos: selectedConditions });
  };

  const localeConditions =
    conditions?.[isSpanish ? 'es_choices' : 'en_choices'];

  return (
    <Dialog open={open} onOpenChange={onDialogClose}>
      <DialogContent className={cn('max-w-lg sm:px-4')} hideClose>
        <DialogHeader>
          <DialogTitle className='text-center'>
            {t('browse_medications')}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            {t('search_medications')}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div>
            <Input
              onChange={(e) => onSearch(e.target.value)}
              value={searchValue}
              placeholder={`${t('search_conditions')}...`}
            />
            <div className='mt-4'>
              {isLoading ? (
                <Loader className='h-[30vh]' loaderSize='size-6' />
              ) : localeConditions?.length ? (
                <ScrollArea className='h-[30vh]'>
                  <div className='space-y-2'>
                    {localeConditions.map((condition: string) => {
                      const isSelected = selectedConditions.includes(condition);
                      return (
                        <motion.div
                          key={condition}
                          onClick={() => onHandleSelection(condition)}
                          className={cn(
                            'w-full border-[2px] rounded-2xl px-4 py-2 flex items-center justify-between cursor-pointer text-gray-600 gap-4',
                            isSelected &&
                              'border-primary text-primary bg-[#E1EBFF]'
                          )}
                          whileTap={{ scale: 0.98 }}
                        >
                          <p className='text-sm leading-5'>{condition}</p>

                          <Checkmark isSelected={isSelected} />
                        </motion.div>
                      );
                    })}
                  </div>
                </ScrollArea>
              ) : (
                <div className='h-[30vh] flex-col items-center justify-center text-sm text-[#656D76] flex space-y-2'>
                  <div className='flex items-center justify-center rounded-md bg-rose-100 size-10'>
                    <X className='size-6 text-rose-500' />
                  </div>
                  <p>{t('condition_not_found')}</p>
                  <Button onClick={onAdd} size={'sm'}>
                    {t('add_anyway')}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className='flex items-center justify-between'>
              <h1 className='font-semibold'>{t('selected')}</h1>
              <Button
                variant='link'
                className='text-sm underline transition-all duration-300 hover:no-underline text-rose-500 h-9'
                disabled={!selectedConditions.length}
                onClick={onClearAll}
              >
                {t('clear_all')}
              </Button>
            </div>
            <ScrollArea className='h-[20vh]'>
              <div className='flex flex-wrap gap-2'>
                {selectedConditions?.length ? (
                  selectedConditions.map((condition: string) => {
                    return (
                      <motion.div
                        key={condition}
                        className={cn(
                          'border border-gray-300 text-sm font-medium text-[#656D76] px-4 rounded-xl h-fit py-2 min-h-8 flex items-center justify-between cursor-pointer transition-all duration-150 gap-1.5'
                        )}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.15 }}
                        onClick={() => onHandleSelection(condition)}
                      >
                        <p className='leading-5'>{condition}</p>
                        <X className='w-4 h-4' />
                      </motion.div>
                    );
                  })
                ) : (
                  <div>
                    <div
                      className={cn(
                        'bg-[#F3F4F6] text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-center cursor-pointer transition-all duration-150'
                      )}
                    >
                      <p className='leading-5'>{t('none')}</p>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          <Button
            onClick={onApply}
            className='w-full'
            disabled={!selectedConditions.length || isPending}
          >
            {isPending && <Loader2 className='mr-2 animate-spin size-4' />}
            {t('apply')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
